import React, { useState, useEffect, useRef } from 'react';
import AreaSearchModal from './AreaSearchModal';
import RemoteFilterDropdown from './RemoteFilterModal';
import SkillsFilterDropdown from './SkillsFilterModal';
import OccupationsFilterDropdown from './OccupationsFilterModal';
import IndustriesFilterDropdown from './IndustriesFilterModal';
import WorkDaysFilterDropdown from './WorkDaysFilterModal';
import CharacteristicsFilterDropdown from './CharacteristicsFilterModal';
import JobSourcesFilterDropdown from './JobSourcesFilterModal';
import MobileSearchModal from './MobileSearchModal';

interface TopSearchBarProps {
  keyword: string;
  prefectureIds: number[];
  priceOver?: number;
  isRecruiting: boolean;
  remoteTypes: string[];
  skillIds: number[];
  occupationIds: number[];
  industryIds: number[];
  workdayTypes: string[];
  characteristicIds: number[];
  jobSourceIds: number[];
  onSearch: (filters: {
    keyword: string;
    prefecture_ids: number[];
    price_over?: number;
    is_recruiting: boolean;
    remote_types: string[];
    skill_ids: number[];
    occupation_ids: number[];
    industry_ids: number[];
    workday_types: string[];
    characteristic_ids: number[];
    job_source_ids: number[];
  }) => void;
}

const TopSearchBar: React.FC<TopSearchBarProps> = ({ 
  keyword, 
  prefectureIds,
  priceOver,
  isRecruiting,
  remoteTypes,
  skillIds,
  occupationIds,
  industryIds,
  workdayTypes,
  characteristicIds,
  jobSourceIds,
  onSearch 
}) => {
  const [inputValue, setInputValue] = useState(keyword);
  const [selectedPrefectureIds, setSelectedPrefectureIds] = useState<number[]>(prefectureIds);
  const [selectedPriceOver, setSelectedPriceOver] = useState<number | undefined>(priceOver);
  const [recruitingOnly, setRecruitingOnly] = useState(isRecruiting);
  const [selectedRemoteTypes, setSelectedRemoteTypes] = useState<string[]>(remoteTypes);
  const [selectedSkillIds, setSelectedSkillIds] = useState<number[]>(skillIds);
  const [selectedOccupationIds, setSelectedOccupationIds] = useState<number[]>(occupationIds);
  const [selectedIndustryIds, setSelectedIndustryIds] = useState<number[]>(industryIds);
  const [selectedWorkdayTypes, setSelectedWorkdayTypes] = useState<string[]>(workdayTypes);
  const [selectedCharacteristicIds, setSelectedCharacteristicIds] = useState<number[]>(characteristicIds);
  const [selectedCharacteristicNames, setSelectedCharacteristicNames] = useState<string[]>([]);
  const [isLoadingCharacteristics, setIsLoadingCharacteristics] = useState(false);
  const [selectedJobSourceIds, setSelectedJobSourceIds] = useState<number[]>(jobSourceIds);
  
  const [showAreaModal, setShowAreaModal] = useState(false);
  const [showRemoteDropdown, setShowRemoteDropdown] = useState(false);
  const [showSkillsDropdown, setShowSkillsDropdown] = useState(false);
  const [showOccupationsDropdown, setShowOccupationsDropdown] = useState(false);
  const [showIndustriesDropdown, setShowIndustriesDropdown] = useState(false);
  const [showWorkdaysDropdown, setShowWorkdaysDropdown] = useState(false);
  const [showCharacteristicsDropdown, setShowCharacteristicsDropdown] = useState(false);
  const [showJobSourcesDropdown, setShowJobSourcesDropdown] = useState(false);
  const [showMobileSearchModal, setShowMobileSearchModal] = useState(false);
  
  const remoteButtonRef = useRef<HTMLButtonElement>(null);
  const skillsButtonRef = useRef<HTMLButtonElement>(null);
  const occupationsButtonRef = useRef<HTMLButtonElement>(null);
  const industriesButtonRef = useRef<HTMLButtonElement>(null);
  const workdaysButtonRef = useRef<HTMLButtonElement>(null);
  const characteristicsButtonRef = useRef<HTMLButtonElement>(null);
  const jobSourcesButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setInputValue(keyword);
  }, [keyword]);

  useEffect(() => {
    setSelectedPrefectureIds(prefectureIds);
  }, [prefectureIds]);

  useEffect(() => {
    setSelectedPriceOver(priceOver);
  }, [priceOver]);

  useEffect(() => {
    setRecruitingOnly(isRecruiting);
  }, [isRecruiting]);

  useEffect(() => {
    setSelectedRemoteTypes(remoteTypes);
  }, [remoteTypes]);

  useEffect(() => {
    setSelectedSkillIds(skillIds);
  }, [skillIds]);

  useEffect(() => {
    setSelectedOccupationIds(occupationIds);
  }, [occupationIds]);

  useEffect(() => {
    setSelectedIndustryIds(industryIds);
  }, [industryIds]);

  useEffect(() => {
    setSelectedWorkdayTypes(workdayTypes);
  }, [workdayTypes]);

  useEffect(() => {
    setSelectedJobSourceIds(jobSourceIds);
  }, [jobSourceIds]);

  useEffect(() => {
    setSelectedCharacteristicIds(characteristicIds);
    
    // 初期化時に選択された特徴の名前を取得
    if (characteristicIds.length > 0) {
      setIsLoadingCharacteristics(true);
      fetch('/api/v1/characteristics')
        .then(response => response.json())
        .then(data => {
          const names = characteristicIds.map(id => {
            const characteristic = data.find((c: any) => c.id === id);
            return characteristic ? characteristic.name : '';
          }).filter(name => name);
          setSelectedCharacteristicNames(names);
          setIsLoadingCharacteristics(false);
        })
        .catch(error => {
          console.error('Failed to fetch characteristic names:', error);
          setIsLoadingCharacteristics(false);
        });
    } else {
      setSelectedCharacteristicNames([]);
    }
  }, [characteristicIds]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch();
  };

  const handleSearch = () => {
    // 検索パラメータを構築してonSearchコールバックを呼び出し
    const searchFilters = {
      keyword: inputValue,
      prefecture_ids: selectedPrefectureIds,
      price_over: selectedPriceOver,
      is_recruiting: recruitingOnly,
      remote_types: selectedRemoteTypes,
      skill_ids: selectedSkillIds,
      occupation_ids: selectedOccupationIds,
      industry_ids: selectedIndustryIds,
      workday_types: selectedWorkdayTypes,
      characteristic_ids: selectedCharacteristicIds,
      job_source_ids: selectedJobSourceIds
    };
    
    // onSearchコールバックを呼び出してReact状態を更新
    onSearch(searchFilters);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value ? Number(e.target.value) : undefined;
    setSelectedPriceOver(value);
    // Trigger search immediately after changing price
    const searchFilters = {
      keyword: inputValue,
      prefecture_ids: selectedPrefectureIds,
      price_over: value,
      is_recruiting: recruitingOnly,
      remote_types: selectedRemoteTypes,
      skill_ids: selectedSkillIds,
      occupation_ids: selectedOccupationIds,
      industry_ids: selectedIndustryIds,
      workday_types: selectedWorkdayTypes,
      characteristic_ids: selectedCharacteristicIds,
      job_source_ids: selectedJobSourceIds
    };
    onSearch(searchFilters);
  };

  const handleRecruitingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setRecruitingOnly(checked);
    // Trigger search immediately after changing recruiting filter
    const searchFilters = {
      keyword: inputValue,
      prefecture_ids: selectedPrefectureIds,
      price_over: selectedPriceOver,
      is_recruiting: checked,
      remote_types: selectedRemoteTypes,
      skill_ids: selectedSkillIds,
      occupation_ids: selectedOccupationIds,
      industry_ids: selectedIndustryIds,
      workday_types: selectedWorkdayTypes,
      characteristic_ids: selectedCharacteristicIds,
      job_source_ids: selectedJobSourceIds
    };
    onSearch(searchFilters);
  };

  const handleAreaApply = (prefIds: number[]) => {
    setSelectedPrefectureIds(prefIds);
    // Trigger search immediately after applying area filter
    const searchFilters = {
      keyword: inputValue,
      prefecture_ids: prefIds,
      price_over: selectedPriceOver,
      is_recruiting: recruitingOnly,
      remote_types: selectedRemoteTypes,
      skill_ids: selectedSkillIds,
      occupation_ids: selectedOccupationIds,
      industry_ids: selectedIndustryIds,
      workday_types: selectedWorkdayTypes,
      characteristic_ids: selectedCharacteristicIds,
      job_source_ids: selectedJobSourceIds
    };
    onSearch(searchFilters);
  };

  const handleRemoteApply = (remoteTypes: string[]) => {
    setSelectedRemoteTypes(remoteTypes);
    const searchFilters = {
      keyword: inputValue,
      prefecture_ids: selectedPrefectureIds,
      price_over: selectedPriceOver,
      is_recruiting: recruitingOnly,
      remote_types: remoteTypes,
      skill_ids: selectedSkillIds,
      occupation_ids: selectedOccupationIds,
      industry_ids: selectedIndustryIds,
      workday_types: selectedWorkdayTypes,
      characteristic_ids: selectedCharacteristicIds,
      job_source_ids: selectedJobSourceIds
    };
    onSearch(searchFilters);
  };

  const handleSkillsApply = (skillIds: number[]) => {
    setSelectedSkillIds(skillIds);
    const searchFilters = {
      keyword: inputValue,
      prefecture_ids: selectedPrefectureIds,
      price_over: selectedPriceOver,
      is_recruiting: recruitingOnly,
      remote_types: selectedRemoteTypes,
      skill_ids: skillIds,
      occupation_ids: selectedOccupationIds,
      industry_ids: selectedIndustryIds,
      workday_types: selectedWorkdayTypes,
      characteristic_ids: selectedCharacteristicIds,
      job_source_ids: selectedJobSourceIds
    };
    onSearch(searchFilters);
  };

  const handleOccupationsApply = (occupationIds: number[]) => {
    setSelectedOccupationIds(occupationIds);
    const searchFilters = {
      keyword: inputValue,
      prefecture_ids: selectedPrefectureIds,
      price_over: selectedPriceOver,
      is_recruiting: recruitingOnly,
      remote_types: selectedRemoteTypes,
      skill_ids: selectedSkillIds,
      occupation_ids: occupationIds,
      industry_ids: selectedIndustryIds,
      workday_types: selectedWorkdayTypes,
      characteristic_ids: selectedCharacteristicIds,
      job_source_ids: selectedJobSourceIds
    };
    onSearch(searchFilters);
  };

  const handleIndustriesApply = (industryIds: number[]) => {
    setSelectedIndustryIds(industryIds);
    const searchFilters = {
      keyword: inputValue,
      prefecture_ids: selectedPrefectureIds,
      price_over: selectedPriceOver,
      is_recruiting: recruitingOnly,
      remote_types: selectedRemoteTypes,
      skill_ids: selectedSkillIds,
      occupation_ids: selectedOccupationIds,
      industry_ids: industryIds,
      workday_types: selectedWorkdayTypes,
      characteristic_ids: selectedCharacteristicIds,
      job_source_ids: selectedJobSourceIds
    };
    onSearch(searchFilters);
  };

  const handleWorkdaysApply = (workdayTypes: string[]) => {
    setSelectedWorkdayTypes(workdayTypes);
    const searchFilters = {
      keyword: inputValue,
      prefecture_ids: selectedPrefectureIds,
      price_over: selectedPriceOver,
      is_recruiting: recruitingOnly,
      remote_types: selectedRemoteTypes,
      skill_ids: selectedSkillIds,
      occupation_ids: selectedOccupationIds,
      industry_ids: selectedIndustryIds,
      workday_types: workdayTypes,
      characteristic_ids: selectedCharacteristicIds,
      job_source_ids: selectedJobSourceIds
    };
    onSearch(searchFilters);
  };

  const handleCharacteristicsApply = (characteristicIds: number[]) => {
    setSelectedCharacteristicIds(characteristicIds);
    
    // 特徴の名前を取得
    if (characteristicIds.length > 0) {
      setIsLoadingCharacteristics(true);
      fetch('/api/v1/characteristics')
        .then(response => response.json())
        .then(data => {
          const names = characteristicIds.map(id => {
            const characteristic = data.find((c: any) => c.id === id);
            return characteristic ? characteristic.name : '';
          }).filter(name => name);
          setSelectedCharacteristicNames(names);
          setIsLoadingCharacteristics(false);
        })
        .catch(error => {
          console.error('Failed to fetch characteristic names:', error);
          setIsLoadingCharacteristics(false);
        });
    } else {
      setSelectedCharacteristicNames([]);
    }
    
    const searchFilters = {
      keyword: inputValue,
      prefecture_ids: selectedPrefectureIds,
      price_over: selectedPriceOver,
      is_recruiting: recruitingOnly,
      remote_types: selectedRemoteTypes,
      skill_ids: selectedSkillIds,
      occupation_ids: selectedOccupationIds,
      industry_ids: selectedIndustryIds,
      workday_types: selectedWorkdayTypes,
      characteristic_ids: characteristicIds,
      job_source_ids: selectedJobSourceIds
    };
    onSearch(searchFilters);
  };

  const handleJobSourcesApply = (jobSourceIds: number[]) => {
    setSelectedJobSourceIds(jobSourceIds);
    const searchFilters = {
      keyword: inputValue,
      prefecture_ids: selectedPrefectureIds,
      price_over: selectedPriceOver,
      is_recruiting: recruitingOnly,
      remote_types: selectedRemoteTypes,
      skill_ids: selectedSkillIds,
      occupation_ids: selectedOccupationIds,
      industry_ids: selectedIndustryIds,
      workday_types: selectedWorkdayTypes,
      characteristic_ids: selectedCharacteristicIds,
      job_source_ids: jobSourceIds
    };
    onSearch(searchFilters);
  };

  // 価格帯オプション（8段階）
  const priceOptions = [
    { value: '', label: '単価' },
    { value: '300000', label: '30万円～' },
    { value: '400000', label: '40万円～' },
    { value: '500000', label: '50万円～' },
    { value: '600000', label: '60万円～' },
    { value: '700000', label: '70万円～' },
    { value: '800000', label: '80万円～' },
    { value: '900000', label: '90万円～' },
    { value: '1000000', label: '100万円～' }
  ];

  // 現在の日付を取得する関数
  const getCurrentDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const day = now.getDate();
    const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
    const weekday = weekdays[now.getDay()];
    
    return `${year}年${month}月${day}日（${weekday}）`;
  };

  return (
    <>
      <div className="bg-light border-bottom py-2 py-md-3 sticky-top" style={{ zIndex: 1020 }}>
        <div className="container-fluid">
          {/* 最終更新日表示（スマートフォン表示時は非表示） */}
          <div className="row mb-2 d-none d-md-block">
            <div className="col-12">
              <p className="text-muted small mb-0">最終更新日：{getCurrentDate()}</p>
            </div>
          </div>

          {/* モバイル検索ボタン */}
          <div className="d-md-none mb-0">
            <div className="input-group">
              <span className="input-group-text bg-white">
                <i className="bi bi-search text-muted"></i>
              </span>
              <button
                type="button"
                className="form-control text-start bg-white border"
                onClick={() => {
                  console.log('Mobile search button clicked, opening modal');
                  setShowMobileSearchModal(true);
                }}
                style={{ 
                  backgroundColor: '#f8f9fa', 
                  borderColor: '#6c757d', 
                  color: '#495057',
                  border: '1px solid #6c757d',
                  cursor: 'pointer',
                  height: '38px'
                }}
              >
                検索条件を指定する
              </button>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="d-none d-md-block">
            <div className="row g-2 align-items-center">
              {/* エリア選択 */}
              <div className="col-12 col-md-3">
                <div className="input-group">
                  <span className="input-group-text bg-white">
                    <i className="bi bi-geo-alt text-muted"></i>
                  </span>
                  <button
                    type="button"
                    className="form-control text-start bg-white border"
                    onClick={() => setShowAreaModal(true)}
                    style={{ 
                      backgroundColor: '#f8f9fa', 
                      borderColor: '#6c757d', 
                      color: '#495057',
                      border: '1px solid #6c757d',
                      cursor: 'pointer'
                    }}
                  >
                    {selectedPrefectureIds.length > 0 
                      ? `選択済み (${selectedPrefectureIds.length})` 
                      : 'エリアを選択'
                    }
                  </button>
                </div>
              </div>

              {/* 価格下限 */}
              <div className="col-12 col-md-auto">
                <select
                  className="form-select"
                  value={selectedPriceOver || ''}
                  onChange={handlePriceChange}
                  style={{ minWidth: '140px' }}
                >
                  {priceOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* キーワード検索 */}
              <div className="col-12 col-md-3">
                <div className="input-group">
                  <span className="input-group-text bg-white">
                    <i className="bi bi-search text-muted"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="キーワードで検索"
                    value={inputValue}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* 検索ボタン */}
              <div className="col-12 col-md-auto">
                <button 
                  type="submit" 
                  className="btn btn-primary w-100"
                  style={{ minWidth: '100px' }}
                >
                  <i className="bi bi-search me-1"></i>
                  検索
                </button>
              </div>

            </div>


            {/* 追加フィルターボタン行 */}
            <div className="row g-2 mt-2 d-none d-md-block">
              <div className="col-12">
                <div className="d-flex flex-wrap gap-2">
                  {/* 募集中 */}
                  <button
                    type="button"
                    className="btn btn-sm"
                    style={{
                      backgroundColor: recruitingOnly ? '#00bb94' : '#f8f9fa',
                      borderColor: recruitingOnly ? '#00bb94' : '#6c757d',
                      color: recruitingOnly ? '#fff' : '#495057',
                      border: '1px solid',
                      fontWeight: '500'
                    }}
                    onClick={() => {
                      const newRecruitingOnly = !recruitingOnly;
                      setRecruitingOnly(newRecruitingOnly);
                      const searchFilters = {
                        keyword: inputValue,
                        prefecture_ids: selectedPrefectureIds,
                        price_over: selectedPriceOver,
                        is_recruiting: newRecruitingOnly,
                        remote_types: selectedRemoteTypes,
                        skill_ids: selectedSkillIds,
                        occupation_ids: selectedOccupationIds,
                        industry_ids: selectedIndustryIds,
                        workday_types: selectedWorkdayTypes,
                        characteristic_ids: selectedCharacteristicIds
                      };
                      onSearch(searchFilters);
                    }}
                  >
                    <i className="fas fa-check-circle me-1"></i>
                    募集中
                  </button>

                  {/* 勤務形態 */}
                  <div className="position-relative">
                    <button
                      ref={remoteButtonRef}
                      type="button"
                      className="btn btn-sm"
                      style={{ 
                        backgroundColor: '#f8f9fa', 
                        borderColor: '#6c757d', 
                        color: '#495057',
                        border: '1px solid #6c757d'
                      }}
                      onClick={() => setShowRemoteDropdown(!showRemoteDropdown)}
                    >
                      <i className="fas fa-home me-1"></i>
                      勤務形態
                      {selectedRemoteTypes.length > 0 && (
                        <span className="badge bg-primary ms-1">
                          {selectedRemoteTypes.length}
                        </span>
                      )}
                    </button>
                    <RemoteFilterDropdown
                      show={showRemoteDropdown}
                      onClose={() => setShowRemoteDropdown(false)}
                      onApply={handleRemoteApply}
                      selectedRemoteTypes={selectedRemoteTypes}
                      buttonRef={remoteButtonRef}
                    />
                  </div>

                  {/* スキル */}
                  <div className="position-relative">
                    <button
                      ref={skillsButtonRef}
                      type="button"
                      className="btn btn-sm"
                      style={{ 
                        backgroundColor: '#f8f9fa', 
                        borderColor: '#6c757d', 
                        color: '#495057',
                        border: '1px solid #6c757d'
                      }}
                      onClick={() => setShowSkillsDropdown(!showSkillsDropdown)}
                    >
                      <i className="fas fa-code me-1"></i>
                      スキル
                      {selectedSkillIds.length > 0 && (
                        <span className="badge bg-primary ms-1">
                          {selectedSkillIds.length}
                        </span>
                      )}
                    </button>
                    <SkillsFilterDropdown
                      show={showSkillsDropdown}
                      onHide={() => setShowSkillsDropdown(false)}
                      onApply={handleSkillsApply}
                      selectedSkillIds={selectedSkillIds}
                      buttonRef={skillsButtonRef}
                    />
                  </div>

                  {/* 職種 */}
                  <div className="position-relative">
                    <button
                      ref={occupationsButtonRef}
                      type="button"
                      className="btn btn-sm"
                      style={{ 
                        backgroundColor: '#f8f9fa', 
                        borderColor: '#6c757d', 
                        color: '#495057',
                        border: '1px solid #6c757d'
                      }}
                      onClick={() => setShowOccupationsDropdown(!showOccupationsDropdown)}
                    >
                      <i className="fas fa-briefcase me-1"></i>
                      職種
                      {selectedOccupationIds.length > 0 && (
                        <span className="badge bg-primary ms-1">
                          {selectedOccupationIds.length}
                        </span>
                      )}
                    </button>
                    <OccupationsFilterDropdown
                      show={showOccupationsDropdown}
                      onHide={() => setShowOccupationsDropdown(false)}
                      onApply={handleOccupationsApply}
                      selectedOccupationIds={selectedOccupationIds}
                      buttonRef={occupationsButtonRef}
                    />
                  </div>

                  {/* 業界 */}
                  <div className="position-relative">
                    <button
                      ref={industriesButtonRef}
                      type="button"
                      className="btn btn-sm"
                      style={{ 
                        backgroundColor: '#f8f9fa', 
                        borderColor: '#6c757d', 
                        color: '#495057',
                        border: '1px solid #6c757d'
                      }}
                      onClick={() => setShowIndustriesDropdown(!showIndustriesDropdown)}
                    >
                      <i className="fas fa-industry me-1"></i>
                      業界
                      {selectedIndustryIds.length > 0 && (
                        <span className="badge bg-primary ms-1">
                          {selectedIndustryIds.length}
                        </span>
                      )}
                    </button>
                    <IndustriesFilterDropdown
                      show={showIndustriesDropdown}
                      onClose={() => setShowIndustriesDropdown(false)}
                      onApply={handleIndustriesApply}
                      selectedIndustryIds={selectedIndustryIds}
                      buttonRef={industriesButtonRef}
                    />
                  </div>

                  {/* 週日数 */}
                  <div className="position-relative">
                    <button
                      ref={workdaysButtonRef}
                      type="button"
                      className="btn btn-sm"
                      style={{ 
                        backgroundColor: '#f8f9fa', 
                        borderColor: '#6c757d', 
                        color: '#495057',
                        border: '1px solid #6c757d'
                      }}
                      onClick={() => setShowWorkdaysDropdown(!showWorkdaysDropdown)}
                    >
                      <i className="fas fa-calendar-week me-1"></i>
                      週日数
                      {selectedWorkdayTypes.length > 0 && (
                        <span className="badge bg-primary ms-1">
                          {selectedWorkdayTypes.length}
                        </span>
                      )}
                    </button>
                    <WorkDaysFilterDropdown
                      show={showWorkdaysDropdown}
                      onClose={() => setShowWorkdaysDropdown(false)}
                      onApply={handleWorkdaysApply}
                      selectedWorkdayTypes={selectedWorkdayTypes}
                      buttonRef={workdaysButtonRef}
                    />
                  </div>

                  {/* 特徴 */}
                  <div className="position-relative">
                    <button
                      ref={characteristicsButtonRef}
                      type="button"
                      className="btn btn-sm"
                      style={{ 
                        backgroundColor: '#f8f9fa', 
                        borderColor: '#6c757d', 
                        color: '#495057',
                        border: '1px solid #6c757d'
                      }}
                      onClick={() => setShowCharacteristicsDropdown(!showCharacteristicsDropdown)}
                    >
                      <i className="fas fa-star me-1"></i>
                      特徴
                      {selectedCharacteristicIds.length > 0 && (
                        <span className="badge bg-primary ms-1">
                          {selectedCharacteristicIds.length}
                        </span>
                      )}
                    </button>
                    <CharacteristicsFilterDropdown
                      show={showCharacteristicsDropdown}
                      onClose={() => setShowCharacteristicsDropdown(false)}
                      onApply={handleCharacteristicsApply}
                      selectedCharacteristicIds={selectedCharacteristicIds}
                      buttonRef={characteristicsButtonRef}
                    />
                  </div>

                  {/* フリーランスエージェント */}
                  <div className="position-relative">
                    <button
                      ref={jobSourcesButtonRef}
                      type="button"
                      className="btn btn-sm"
                      style={{ 
                        backgroundColor: '#f8f9fa', 
                        borderColor: '#6c757d', 
                        color: '#495057',
                        border: '1px solid #6c757d'
                      }}
                      onClick={() => setShowJobSourcesDropdown(!showJobSourcesDropdown)}
                    >
                      <i className="fas fa-building me-1"></i>
                      フリーランスエージェント
                      {selectedJobSourceIds.length > 0 && (
                        <span className="badge bg-primary ms-1">
                          {selectedJobSourceIds.length}
                        </span>
                      )}
                    </button>
                    <JobSourcesFilterDropdown
                      show={showJobSourcesDropdown}
                      onHide={() => setShowJobSourcesDropdown(false)}
                      onApply={handleJobSourcesApply}
                      selectedJobSourceIds={selectedJobSourceIds}
                      buttonRef={jobSourcesButtonRef}
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* モーダル群 */}
      <AreaSearchModal
        show={showAreaModal}
        selectedPrefectureIds={selectedPrefectureIds}
        onClose={() => setShowAreaModal(false)}
        onApply={handleAreaApply}
        onImmediateSearch={handleAreaApply}
      />

      {/* モバイル検索モーダル */}
      <MobileSearchModal
        show={showMobileSearchModal}
        onClose={() => setShowMobileSearchModal(false)}
        onSearch={onSearch}
        initialFilters={{
          keyword: inputValue,
          prefecture_ids: selectedPrefectureIds,
          price_over: selectedPriceOver,
          is_recruiting: recruitingOnly,
          remote_types: selectedRemoteTypes,
          skill_ids: selectedSkillIds,
          occupation_ids: selectedOccupationIds,
          industry_ids: selectedIndustryIds,
          workday_types: selectedWorkdayTypes,
          characteristic_ids: selectedCharacteristicIds,
          job_source_ids: selectedJobSourceIds
        }}
      />

    </>
  );
};

export default TopSearchBar;
